import { Box } from 'grommet';
import { useEffect, useState } from 'react';
import cross from '../../../assets/cross.svg';
import discount from '../../../assets/discount.svg';
import {
  validateDiscountCode,
  resetDiscountValidation
} from '../../../redux/api-v2/DiscountsApi';
import {
  calculatePricing,
  resetPricingCalculation
} from '../../../redux/api-v2/PricingApi';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  OrderFlow,
  setDiscount,
  setProducts
} from '../../../redux/orderFlow/orderFlowSlice';
import { getMonthsNumber } from '../../../utils/prices-calculator';
import { SessionManager } from '../../../utils/session-manager';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/store';
import { PricingCalculationRequest } from '@oncampusabroad/api';
import { createDateFromString } from '../../../utils/date-formatter';

import { TextInput } from '../../atoms/text-input/text-input';
import {
  TablePrices,
  DiscountContainer,
  DiscountButton,
  PriceContainer,
  Separationbar,
  DiscountBox,
  DiscountIcon,
  CrossIcon
} from './order-summary-styles';
import Tooltip from '../../atoms/tooltip/tooltip';

const OrderSummaryCalculation = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const orderDetails = useAppSelector(state => state.orderFlow);
  const currentFlow = useAppSelector(state => state.orderFlow.flow);
  const discountCodeValidationApi = useAppSelector(
    state => state.discounts.discountValidation
  );
  const pricingCalculation = useAppSelector(
    state => state.pricing.pricingCalculation
  );

  const months = useAppSelector(
    state => state.pricing.pricingCalculation.response.totalMonths
  );

  const [discountCode, setDiscountCode] = useState<string>('');

  const apiProductsResponse = useAppSelector(
    (state: RootState) => state.products.products.response
  );
  const apiProducts = Array.isArray(apiProductsResponse)
    ? apiProductsResponse
    : [];

  const sportsProduct = apiProducts.find(
    product => product.productId === 'SPORTS2024'
  );
  const travelProduct = apiProducts.find(
    product => product.productId === 'TRAVEL2024'
  );

  const hasSportsProduct = orderDetails.orderedProducts?.includes('SPORTS2024');
  const hasHolidaysProduct =
    orderDetails.orderedProducts?.includes('TRAVEL2024');
  const hasHealthProduct = orderDetails.orderedProducts?.includes('MAIN2024');

  const [discountCodeError, setDiscountCodeError] = useState(false);
  const handleValidationDiscountCode = async () => {
    discountCode
      ? dispatch(validateDiscountCode(discountCode))
      : setDiscountCodeError(true);
  };

  useEffect(() => {
    const { loading, response } = discountCodeValidationApi;
    const isApiCallSucceeded = loading === 'succeeded';
    const isDiscountValid = response && response.valid;

    if (isApiCallSucceeded) {
      dispatch(resetDiscountValidation());

      if (isDiscountValid) {
        dispatch(
          setDiscount({
            discountCode: discountCode
          })
        );
      } else if (discountCode && discountCode.trim() !== '') {
        setDiscountCodeError(true);
      }
    }
  }, [
    discountCodeValidationApi.loading,
    discountCodeValidationApi.response,
    discountCode
  ]);
  const relatedOrderId = useAppSelector(
    (state: RootState) => state.orders.order.response.id
  );
  const calculatePriceFromApi = () => {
    const productsToOrder = orderDetails.orderedProducts
      ? [...orderDetails.orderedProducts]
      : [];

    if (
      currentFlow === OrderFlow.Flow1 &&
      !productsToOrder.includes('MAIN2024')
    ) {
      productsToOrder.push('MAIN2024');
    }

    if (productsToOrder.length === 0) {
      return;
    }

    const productObjectIds = productsToOrder
      .map(productId => {
        const product = apiProducts.find(p => p.productId === productId);
        return product?.id;
      })
      .filter((id): id is string => id !== undefined);

    if (productObjectIds.length === 0) {
      console.error('No valid product IDs found');
      return;
    }

    const request: PricingCalculationRequest = {
      departureDate: createDateFromString(orderDetails.departureDate),
      returnDate: createDateFromString(orderDetails.returnDate),
      withEHIC: orderDetails.withEHIC,
      productsOrdered: productObjectIds,
      homeCountry: orderDetails.homeCountryCode,
      destinationCountry: orderDetails.destinationCountryCode,
      discountCode: orderDetails.discountCode,
      relatedOrderId:
        relatedOrderId && relatedOrderId.length > 0 ? relatedOrderId : undefined
    };

    dispatch(calculatePricing(request));
  };

  useEffect(() => {
    if (currentFlow === OrderFlow.Flow1) {
      calculatePriceFromApi();
    }
    calculatePriceFromApi();

    return () => {
      dispatch(resetPricingCalculation());
    };
  }, [
    orderDetails.departureDate,
    orderDetails.returnDate,
    orderDetails.withEHIC,
    orderDetails.orderedProducts,
    orderDetails.discountCode,
    currentFlow
  ]);

  const deleteDiscountCode = () => {
    setDiscountCode('');
    dispatch(
      setDiscount({
        discountCode: ''
      })
    );
  };

  const hasDiscountCode =
    orderDetails.discountCode && orderDetails.discountCode.trim() !== '';

  const isCalculating =
    currentFlow === OrderFlow.Flow1 && pricingCalculation.loading === 'pending';

  const calculateFlow2Total = () => {
    if (currentFlow !== OrderFlow.Flow2) return 0;

    let total = 0;

    orderDetails.orderedProducts?.forEach(productId => {
      const product = apiProducts.find(p => p.productId === productId);
      if (product) {
        const price = orderDetails.withEHIC
          ? product.priceWithEHIC
          : product.priceWithoutEHIC;
        total += price || 0;
      }
    });

    return total;
  };

  const totalAmount =
    currentFlow === OrderFlow.Flow2
      ? calculateFlow2Total()
      : pricingCalculation.response?.totalAmount;

  const totalAmountWithDiscount =
    pricingCalculation.response?.totalAmountWithDiscount;
  const discountAmount = pricingCalculation.response?.discountAmount;
  const discountPercent = pricingCalculation.response?.discountPercent;

  return (
    <Box>
      {currentFlow === OrderFlow.Flow1 && (
        <TablePrices>
          <p>{t('summary-tab-1')}</p>
          <div>
            <p>
              {orderDetails.withEHIC
                ? t('summary-tab-price-25')
                : t('summary-tab-price-36')}
            </p>
            <p>
              ({months}
              {months === 1
                ? t('summary-tab-2').slice(0, -1)
                : t('summary-tab-2')}
              )
            </p>
          </div>
        </TablePrices>
      )}
      {currentFlow === OrderFlow.Flow1 && (
        <>
          {!hasDiscountCode ? (
            <DiscountContainer>
              <TextInput
                field={{ id: 'discountCode', type: 'string', mandatory: true }}
                onChange={setDiscountCode}
                value={discountCode}
                warning={discountCodeError}
              />
              <DiscountButton
                id='OrderSummaryCalculation_APPLY_Button'
                onClick={() => {
                  SessionManager.setButtonId(
                    'OrderSummaryCalculation_APPLY_Button'
                  );
                  handleValidationDiscountCode();
                }}
              >
                {t('APPLY')}
              </DiscountButton>
            </DiscountContainer>
          ) : (
            <Tooltip
              isDiscount={true}
              tooltipText={t('summary-tooltip-discount-text')}
            >
              <DiscountBox>
                <DiscountIcon src={discount} alt='discount' />
                <p>{orderDetails.discountCode}</p>
                <CrossIcon
                  src={cross}
                  alt='cross'
                  onClick={() => deleteDiscountCode()}
                />
              </DiscountBox>
            </Tooltip>
          )}
        </>
      )}

      {(hasSportsProduct || hasHolidaysProduct) && (
        <PriceContainer>
          {hasSportsProduct && sportsProduct && (
            <TablePrices>
              <p>{t('summary-tab-sports')}</p>
              <p>
                {orderDetails.withEHIC
                  ? sportsProduct.priceWithEHIC
                  : sportsProduct.priceWithoutEHIC}
                €
              </p>
            </TablePrices>
          )}
          {hasHolidaysProduct && travelProduct && (
            <TablePrices>
              <p>{t('summary-tab-holiday')}</p>
              <p>
                {orderDetails.withEHIC
                  ? travelProduct.priceWithEHIC
                  : travelProduct.priceWithoutEHIC}
                €
              </p>
            </TablePrices>
          )}
          <Separationbar />
        </PriceContainer>
      )}
      <TablePrices>
        <p>{t('summary-tab-total')}</p>
        {isCalculating ? (
          <p>{t('calculating')}...</p>
        ) : (
          <>
            {hasDiscountCode && totalAmountWithDiscount !== undefined ? (
              <p>{totalAmountWithDiscount}€</p>
            ) : (
              <p>{totalAmount}€</p>
            )}
          </>
        )}
      </TablePrices>

      {hasDiscountCode && discountAmount > 0 && (
        <TablePrices>
          <p>{t('summary-tab-discount')}</p>
          <p>
            -{discountAmount}€ ({discountPercent}%)
          </p>
        </TablePrices>
      )}
    </Box>
  );
};

export default OrderSummaryCalculation;
